/**
 * localOrderOps — offline-first order operations backed by local SQLite
 *
 * All reads query the local PowerSync SQLite database (kept in sync with
 * Supabase by the main-process sync engine). All Category A writes INSERT /
 * UPDATE / DELETE rows in SQLite; PowerSync queues them and replays them
 * against Supabase when connectivity is restored.
 *
 * Settlement operations are Category A — they write to local SQLite and
 * PowerSync uploads them to Supabase when connectivity is restored.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * SQLite type notes (PowerSync):
 *  - The `id` column is always TEXT (UUID string, or integer-as-string for
 *    SERIAL PKs like outlet_sections and outlet_tables).
 *  - columns declared column.integer → INTEGER affinity in SQLite.
 *  - columns declared column.text   → TEXT affinity.
 *  - JOIN: CAST(orders.table_id AS TEXT) = outlet_tables.id
 *           ↑ INTEGER                        ↑ TEXT ("5", "12", …)
 *  - JOIN: outlet_tables.section_id = outlet_sections.id
 *           ↑ TEXT                      ↑ TEXT  (both serial ints stored as text)
 *  - OPEN status_id = 9  (from payment_methods seed; name = 'OPEN')
 * ──────────────────────────────────────────────────────────────────────────
 */

import {
  DiscountInputType,
  OrderDiscount,
  OrderKotConfirmation,
  OrderMode,
  OrderPaymentMethod,
  OrderSettlement,
  OrderWaiter,
  SalesClient,
  SalesOrder,
  SalesOrderItem,
} from '@/renderer/types/order';
import { PaginatedResponse } from '@/renderer/types/pagination';
import { OutletSection, OutletTable } from '@/renderer/types/section';
import { ApiResponse } from '@shared/types';

import { db } from './database';
import { psExecute, psGet, psGetAll } from './ipcBridge';

// ── Constants ─────────────────────────────────────────────────────────────────

/** payment_methods row id for OPEN orders (seeded in initial migration) */
const OPEN_STATUS_ID = 9;

// ── Helpers ───────────────────────────────────────────────────────────────────

export const toNumber = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

export const nowIso = () => new Date().toISOString();
export const todayDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const orderItemAmountSql = (alias: string): string => `
  CASE
    WHEN CAST(COALESCE(${alias}.amount, 0) AS REAL) > 0
      THEN CAST(${alias}.amount AS REAL)
    ELSE
      CAST(COALESCE(${alias}.quantity, 0) AS REAL) *
      CAST(COALESCE(${alias}.unit_price, 0) AS REAL)
  END
`;

export function genUUID(): string {
  // crypto.randomUUID is available in Electron renderer (Chromium)
  return (
    (crypto as Crypto & { randomUUID?: () => string }).randomUUID?.() ??
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.floor(Math.random() * 16);
      return (c === 'x' ? r : (r % 4) + 8).toString(16);
    })
  );
}

// ── Context object ────────────────────────────────────────────────────────────

/**
 * Fields that live in Redux auth state, not in the local SQLite database.
 * Pass these from the thunk using `state.auth.user`.
 */
export interface LocalOrderContext {
  outletId: string;
  outletName: string;
  companyId: number;
  userId: string;
  /** Display name for the current user (used as waiter / added_by) */
  userName: string;
  /** Outlet-level Kitchen Display Screen capability flag (outlets.is_kds_enabled) */
  isKdsEnabled: boolean;
}

// ── Row mappers ───────────────────────────────────────────────────────────────

/**
 * Map a raw SQLite row (from the ORDER_BASE_SQL query) to SalesOrder.
 * Fields not stored in SQLite (outlet_name, company_id) come from `ctx`.
 */
const mapLocalOrder = (row: any, ctx: LocalOrderContext): SalesOrder => {
  const netAmount = toNumber(row.total_amount) - toNumber(row.discount_amount);
  const balanceAmount = netAmount - toNumber(row.settlement_total);

  return {
    order_id: row.order_id,
    bill_number: toNumber(row.bill_number),
    table_id: toNumber(row.table_id) || 0,
    table_name: row.table_name || '',
    section_id: toNumber(row.section_id) || 0,
    section_name: row.section_name || '',
    outlet_id: row.outlet_id || ctx.outletId,
    outlet_name: ctx.outletName,
    company_id: ctx.companyId,
    date: row.date || todayDate(),
    status_id: toNumber(row.status_id) || OPEN_STATUS_ID,
    status_name: row.status_name || 'OPEN',
    waiter_id: row.waiter_id || ctx.userId,
    waiter_name: row.waiter_name || ctx.userName,
    client_id: row.client_id ?? null,
    client_name: row.client_name ?? null,
    mode: row.mode,
    discount_amount: toNumber(row.discount_amount),
    total_amount: toNumber(row.total_amount),
    net_amount: netAmount,
    item_count: toNumber(row.item_count),
    pending_item_count: toNumber(row.pending_item_count),
    has_pending_items: toNumber(row.pending_item_count) > 0,
    created_by: row.created_by || ctx.userId,
    created_at: row.created_at || nowIso(),
    updated_at: row.updated_at || nowIso(),
    creator_name: null, // not stored locally
    creator_email: null,
    discount_count: toNumber(row.discount_count),
    last_discount_at: row.last_discount_at ?? null,
    settlement_total: toNumber(row.settlement_total),
    settlement_count: toNumber(row.settlement_count),
    settlement_methods: [], // simplified — detail comes from order_settlements
    last_settlement_at: row.last_settlement_at ?? null,
    balance_amount: balanceAmount,
    is_fully_settled: toNumber(row.settlement_count) > 0 && balanceAmount <= 0,
  };
};

// Fallback item name when the menu_items LEFT JOIN misses (row absent from
// local SQLite, or the sync of it hasn't caught up yet) — used both here and
// by localAddOrderItem's own _itemNameHint fallback below, and exported for
// runningOrderThunks.ts's enrichItemNames/buildNameMap, which need to tell
// "joined name resolved" apart from "joined name missing" by identity
// against this exact string.
export const UNAVAILABLE_ITEM_NAME = '(item unavailable)';

const mapLocalOrderItem = (row: any): SalesOrderItem => {
  const quantity = toNumber(row.quantity);
  const unitPrice = toNumber(row.unit_price);
  const storedAmount = toNumber(row.amount);
  // `amount` is GENERATED ALWAYS AS (quantity * unit_price) STORED in Postgres.
  // PowerSync's WASM SQLite may coerce the synced NUMERIC value to null when
  // stored in a TEXT column — recompute from the two source columns as a guard.
  const amount = storedAmount || Math.round(quantity * unitPrice * 100) / 100;
  return {
    id: row.id,
    order_id: row.order_id,
    menu_item_id: row.menu_item_id,
    name: row.item_name ?? UNAVAILABLE_ITEM_NAME,
    department_id: row.department_id ?? null,
    department_name: row.department_name ?? null,
    quantity,
    unit_price: unitPrice,
    amount,
    notes: row.notes ?? null,
    status: row.status,
    kot_id: toNumber(row.kot_id),
    added_by: row.added_by ?? null,
    added_by_name: row.added_by_name || null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
};

// ── Base SQL for order queries ─────────────────────────────────────────────────

/**
 * Reusable SELECT fragment that computes the same aggregate columns as
 * Postgres `orders_view`. Caller must append WHERE / GROUP BY / ORDER BY.
 *
 * Join notes:
 *  - outlet_tables.id is TEXT (PowerSync stores SERIAL as "5", "12"…)
 *  - orders.table_id is INTEGER → CAST AS TEXT for the join
 *  - outlet_sections.id is TEXT, outlet_tables.section_id is TEXT
 *  - payment_methods.id is TEXT (SERIAL → "9" etc.)
 */
const ORDER_SELECT = `
  SELECT
    o.id                                                                        AS order_id,
    o.bill_number,
    o.table_id,
    ot.name                                                                     AS table_name,
    os.id                                                                       AS section_id,
    os.name                                                                     AS section_name,
    o.outlet_id,
    o.mode,
    o.status_id,
    pm.name                                                                     AS status_name,
    o.waiter_id,
    (u.first_name || ' ' || u.last_name)                                        AS waiter_name,
    o.client_id,
    (c.first_name || ' ' || c.last_name)                                        AS client_name,
    o.date,
    o.created_by,
    o.created_at,
    o.updated_at,
    COALESCE(
      (SELECT SUM(${orderItemAmountSql('oi2')}) FROM order_items oi2
       WHERE oi2.order_id = o.id AND oi2.status != 'CANCELLED'), 0)             AS total_amount,
    COALESCE(
      (SELECT SUM(d.amount) FROM discounts d WHERE d.order_id = o.id), 0)       AS discount_amount,
    COALESCE(
      (SELECT COUNT(*) FROM order_items oi2
       WHERE oi2.order_id = o.id AND oi2.status != 'CANCELLED'), 0)             AS item_count,
    COALESCE(
      (SELECT COUNT(*) FROM order_items oi2
       WHERE oi2.order_id = o.id AND oi2.status = 'PENDING'), 0)                AS pending_item_count,
    COALESCE(
      (SELECT COUNT(*) FROM discounts d WHERE d.order_id = o.id), 0)            AS discount_count,
    (SELECT d.updated_at FROM discounts d WHERE d.order_id = o.id
     ORDER BY d.updated_at DESC LIMIT 1)                                        AS last_discount_at,
    COALESCE(
      (SELECT SUM(s.amount) FROM order_settlements s WHERE s.order_id = o.id), 0) AS settlement_total,
    COALESCE(
      (SELECT COUNT(*) FROM order_settlements s WHERE s.order_id = o.id), 0)    AS settlement_count,
    (SELECT s.created_at FROM order_settlements s WHERE s.order_id = o.id
     ORDER BY s.created_at DESC LIMIT 1)                                        AS last_settlement_at
  FROM orders o
  LEFT JOIN outlet_tables  ot ON CAST(o.table_id AS TEXT) = ot.id
  LEFT JOIN outlet_sections os ON ot.section_id = os.id
  LEFT JOIN payment_methods pm ON CAST(o.status_id AS TEXT) = pm.id
  LEFT JOIN users           u  ON o.waiter_id = u.id
  LEFT JOIN clients         c  ON o.client_id = c.id
`;

// ── Reads ─────────────────────────────────────────────────────────────────────

/**
 * Return the single OPEN order for a dine-in table, or null if none exists.
 */
export async function localFetchRunningTableOrder(
  tableId: number,
  ctx: LocalOrderContext,
): Promise<ApiResponse<SalesOrder | null>> {
  try {
    const sql = `${ORDER_SELECT}
      WHERE o.table_id = ? AND o.status_id = ?
      ORDER BY o.created_at DESC LIMIT 1`;

    const row = await psGet<any>(sql, [tableId, OPEN_STATUS_ID]);
    if (!row) return { success: true, data: null };

    return { success: true, data: mapLocalOrder(row, ctx) };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch table order',
      data: null,
    };
  }
}

/**
 * Return all OPEN TAKEAWAY / CASH orders for the outlet.
 */
export async function localFetchOpenTakeAwayOrders(
  outletId: string,
  ctx: LocalOrderContext,
): Promise<ApiResponse<SalesOrder[]>> {
  try {
    const sql = `${ORDER_SELECT}
      WHERE o.outlet_id = ?
        AND o.status_id = ?
        AND o.mode IN ('TAKEAWAY', 'CASH')
      ORDER BY o.created_at DESC`;

    const rows = await psGetAll<any>(sql, [outletId, OPEN_STATUS_ID]);
    return { success: true, data: rows.map((r) => mapLocalOrder(r, ctx)) };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to fetch takeaway orders',
      data: [],
    };
  }
}

/**
 * Fetch a single order by its UUID.
 */
export async function localFetchOrderById(
  orderId: string,
  ctx: LocalOrderContext,
): Promise<ApiResponse<SalesOrder>> {
  try {
    const sql = `${ORDER_SELECT} WHERE o.id = ? LIMIT 1`;
    const row = await psGet<any>(sql, [orderId]);

    if (!row) {
      return { success: false, error: 'Order not found', data: undefined };
    }

    return { success: true, data: mapLocalOrder(row, ctx) };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to fetch order details',
      data: undefined,
    };
  }
}

/**
 * Fetch all items for an order.  Joins menu_items for the item name;
 * department_name is left null (departments table not synced locally).
 */
export async function localFetchOrderItems(
  orderId: string,
): Promise<ApiResponse<SalesOrderItem[]>> {
  try {
    // Mirrors order_items_view: LEFT JOIN menu_items, departments, menu_categories.
    // departments and menu_categories are synced to local SQLite by PowerSync,
    // so department_name and category_name are available offline.
    const sql = `
      SELECT
        oi.id,
        oi.order_id,
        oi.menu_item_id,
        mi.name             AS item_name,
        mi.department_id,
        d.name              AS department_name,
        mi.category_id,
        mc.name             AS category_name,
        oi.quantity,
        oi.unit_price,
        oi.amount,
        oi.notes,
        oi.status,
        oi.kot_id,
        oi.added_by,
        TRIM(COALESCE(au.first_name, '') || ' ' || COALESCE(au.last_name, '')) AS added_by_name,
        oi.created_at,
        oi.updated_at
      FROM order_items oi
      LEFT JOIN menu_items      mi ON mi.id = oi.menu_item_id
      LEFT JOIN departments      d  ON  d.id = mi.department_id
      LEFT JOIN menu_categories mc  ON mc.id = mi.category_id
      LEFT JOIN users           au  ON au.id = oi.added_by
      WHERE oi.order_id = ?
      ORDER BY oi.created_at ASC`;

    const rows = await psGetAll<any>(sql, [orderId]);
    return { success: true, data: rows.map(mapLocalOrderItem) };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch order items',
      data: [],
    };
  }
}

/**
 * Fetch payment methods available for settlement.
 * Filters to the standard settlement methods (mirrors the Supabase query).
 */
export async function localFetchPaymentMethods(): Promise<
  ApiResponse<OrderPaymentMethod[]>
> {
  try {
    const sql = `
      SELECT id, name
      FROM payment_methods
      WHERE name IN ('CASH', 'CARD', 'MOBILE MONEY', 'CHECK', 'EFT')
      ORDER BY CAST(id AS INTEGER) ASC`;

    const rows = await psGetAll<any>(sql);
    return {
      success: true,
      data: rows.map((r) => ({ id: toNumber(r.id), name: r.name || '' })),
    };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to fetch payment methods',
      data: [],
    };
  }
}

/**
 * Fetch a single client by id, with the full set of editable fields
 * (including tin_number/menu_percentage_rate, which some client-derived
 * views like accommodation_guests don't carry). Used to seed the client
 * edit form accurately regardless of where the edit action was triggered.
 */
export async function localFetchClientById(
  clientId: string,
): Promise<ApiResponse<SalesClient>> {
  try {
    const row = await psGet<any>(
      `SELECT id, first_name, last_name, email, contact, address, tin_number, menu_percentage_rate
       FROM clients WHERE id = ? LIMIT 1`,
      [clientId],
    );
    if (!row) {
      return { success: false, error: 'Client not found', data: undefined };
    }
    return {
      success: true,
      data: {
        id: row.id,
        first_name: row.first_name || '',
        last_name: row.last_name || '',
        full_name: `${row.first_name || ''} ${row.last_name || ''}`.trim(),
        email: row.email ?? null,
        contact: row.contact ?? null,
        address: row.address ?? null,
        tin_number: row.tin_number ?? null,
        menu_percentage_rate: toNumber(row.menu_percentage_rate),
      },
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch client',
      data: undefined,
    };
  }
}

/**
 * Fetch clients for the company, with optional name search.
 * SQLite LIKE is case-insensitive by default for ASCII; good enough here.
 */
export async function localFetchClients(opts?: {
  search?: string;
  offset?: number;
  limit?: number;
}): Promise<ApiResponse<SalesClient[]>> {
  const { search, offset = 0, limit } = opts ?? {};

  try {
    const conditions: string[] = [];
    const params: unknown[] = [];

    if (search?.trim()) {
      const term = `%${search.trim()}%`;
      conditions.push(`(c.first_name LIKE ? OR c.last_name LIKE ?)`);
      params.push(term, term);
    }

    const where = conditions.length ? `WHERE ${conditions.join(' AND ')}` : '';
    const limitClause =
      limit !== undefined ? `LIMIT ${limit} OFFSET ${offset}` : '';

    const sql = `
      SELECT
        c.id,
        c.first_name,
        c.last_name,
        c.email,
        c.contact,
        c.address,
        c.tin_number,
        c.menu_percentage_rate
      FROM clients c
      ${where}
      ORDER BY c.first_name ASC, c.last_name ASC
      ${limitClause}`;

    const rows = await psGetAll<any>(sql, params);
    return {
      success: true,
      data: rows.map((r) => ({
        id: r.id,
        first_name: r.first_name || '',
        last_name: r.last_name || '',
        full_name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
        email: r.email ?? null,
        contact: r.contact ?? null,
        address: r.address ?? null,
        tin_number: r.tin_number ?? null,
        menu_percentage_rate: toNumber(r.menu_percentage_rate),
      })),
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch clients',
      data: [],
    };
  }
}

/**
 * Paginated client list, returning a PaginatedResponse<SalesClient> shaped
 * for direct pass-through to <PaginationControls>. Runs the same query as
 * localFetchClients plus a matching COUNT(*) for the total.
 */
export async function localFetchClientsPaginated(opts: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ApiResponse<PaginatedResponse<SalesClient>>> {
  const { search, page = 1, pageSize = 20 } = opts;
  const offset = (page - 1) * pageSize;

  try {
    const conditions: string[] = [];
    const params: unknown[] = [];

    if (search?.trim()) {
      const term = `%${search.trim()}%`;
      conditions.push(
        `(c.first_name LIKE ? OR c.last_name LIKE ? OR c.email LIKE ? OR c.contact LIKE ? OR c.tin_number LIKE ?)`,
      );
      params.push(term, term, term, term, term);
    }

    const where = conditions.length ? `WHERE ${conditions.join(' AND ')}` : '';

    const countRow = await psGet<{ total: number }>(
      `SELECT COUNT(*) AS total FROM clients c ${where}`,
      params,
    );
    const totalItems = toNumber(countRow?.total ?? 0);

    const rows = await psGetAll<any>(
      `SELECT
         c.id,
         c.first_name,
         c.last_name,
         c.email,
         c.contact,
         c.address,
         c.tin_number,
         c.menu_percentage_rate
       FROM clients c
       ${where}
       ORDER BY c.first_name ASC, c.last_name ASC
       LIMIT ? OFFSET ?`,
      [...params, pageSize, offset],
    );

    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: rows.map((r) => ({
          id: r.id,
          first_name: r.first_name || '',
          last_name: r.last_name || '',
          full_name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
          email: r.email ?? null,
          contact: r.contact ?? null,
          address: r.address ?? null,
          tin_number: r.tin_number ?? null,
          menu_percentage_rate: toNumber(r.menu_percentage_rate),
        })),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch clients',
      data: undefined,
    };
  }
}

/**
 * Whether a department has KOT printing enabled. Defaults to true when the
 * department row hasn't synced locally yet, matching the column's DB default
 * so we never silently swallow tickets due to a sync gap.
 */
export async function localGetDepartmentKotEnabled(
  departmentId: string,
): Promise<boolean> {
  const row = await psGet<{ kot_enabled: number }>(
    `SELECT kot_enabled FROM departments WHERE id = ?`,
    [departmentId],
  );
  if (!row) return true;
  return Boolean(row.kot_enabled);
}

/**
 * Fetch waiters for an outlet (users with role = 'waiter').
 */
export async function localFetchWaiters(
  outletId: string,
): Promise<ApiResponse<OrderWaiter[]>> {
  try {
    const sql = `
      SELECT id, first_name, last_name, email, role, outlet_id
      FROM users
      WHERE outlet_id = ? AND role = 'waiter'
      ORDER BY first_name ASC, last_name ASC`;

    const rows = await psGetAll<any>(sql, [outletId]);
    return {
      success: true,
      data: rows.map((r) => ({
        id: r.id,
        first_name: r.first_name || '',
        last_name: r.last_name || '',
        full_name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
        email: r.email ?? null,
        role: r.role || 'waiter',
        outlet_id: r.outlet_id ?? null,
      })),
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to fetch waiters',
      data: [],
    };
  }
}

// ── Writes (Category A — queued by PowerSync for background upload) ────────────

/**
 * Find or create an OPEN dine-in order for the given table.
 * Equivalent to the `get_or_create_table_order` Supabase RPC.
 */
export async function localGetOrCreateTableOrder(
  tableId: number,
  ctx: LocalOrderContext,
  mode: OrderMode = 'DINE',
): Promise<ApiResponse<SalesOrder>> {
  try {
    const now = nowIso();
    let orderId = '';

    // Both the "does an OPEN order already exist for this table" check and
    // the counter-increment + insert run inside a single writeTransaction.
    // PowerSync serializes writeTransaction calls, so this closes the
    // TOCTOU window that previously let two near-simultaneous calls for the
    // same table (a fast double-tap, or two terminals opening the same table
    // within the same read window) each observe "no existing order" and
    // insert a separate duplicate order — the older one then silently hidden
    // by callers' `ORDER BY created_at DESC LIMIT 1`. A second racer now
    // simply reads the first racer's committed row instead of inserting.
    await db.writeTransaction(async (tx) => {
      const existing = await tx.get<{ id: string } | undefined>(
        `SELECT id FROM orders WHERE table_id = ? AND status_id = ?
         ORDER BY created_at DESC LIMIT 1`,
        [tableId, OPEN_STATUS_ID],
      );
      if (existing) {
        orderId = existing.id;
        return;
      }

      orderId = genUUID();
      const counterRow = await tx.get<any>(
        `SELECT last_bill_number FROM outlet_order_counters WHERE id = ? LIMIT 1`,
        [ctx.outletId],
      );
      if (!counterRow) {
        throw new Error(
          'Order counter not found — sync may not be complete. Please wait and try again.',
        );
      }
      const nextBillNumber = toNumber(counterRow.last_bill_number) + 1;

      await tx.execute(
        `UPDATE outlet_order_counters
         SET last_bill_number = ?, updated_at = ?
         WHERE id = ?`,
        [nextBillNumber, now, ctx.outletId],
      );

      await tx.execute(
        `INSERT INTO orders
           (id, bill_number, table_id, outlet_id, date, status_id,
            waiter_id, client_id, mode, created_by, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, NULL, ?, ?, ?, ?)`,
        [
          orderId,
          nextBillNumber,
          tableId,
          ctx.outletId,
          todayDate(),
          OPEN_STATUS_ID,
          ctx.userId,
          mode,
          ctx.userId,
          now,
          now,
        ],
      );
    });

    // Re-read through the full SELECT to get joined fields.
    return localFetchOrderById(orderId, ctx);
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to open table order',
      data: undefined,
    };
  }
}

/**
 * Create a new TAKEAWAY / CASH order for the outlet.
 * Equivalent to the `create_takeaway_order` Supabase RPC.
 */
export async function localCreateTakeawayOrder(
  ctx: LocalOrderContext,
): Promise<ApiResponse<SalesOrder>> {
  try {
    const now = nowIso();
    const orderId = genUUID();

    await db.writeTransaction(async (tx) => {
      const counterRow = await tx.get<any>(
        `SELECT last_bill_number FROM outlet_order_counters WHERE id = ? LIMIT 1`,
        [ctx.outletId],
      );
      if (!counterRow) {
        throw new Error(
          'Order counter not found — sync may not be complete. Please wait and try again.',
        );
      }
      const nextBillNumber = toNumber(counterRow.last_bill_number) + 1;

      await tx.execute(
        `UPDATE outlet_order_counters
         SET last_bill_number = ?, updated_at = ?
         WHERE id = ?`,
        [nextBillNumber, now, ctx.outletId],
      );

      await tx.execute(
        `INSERT INTO orders
           (id, bill_number, table_id, outlet_id, date, status_id,
            waiter_id, client_id, mode, created_by, created_at, updated_at)
         VALUES (?, ?, NULL, ?, ?, ?, ?, NULL, 'CASH', ?, ?, ?)`,
        [
          orderId,
          nextBillNumber,
          ctx.outletId,
          todayDate(),
          OPEN_STATUS_ID,
          ctx.userId,
          ctx.userId,
          now,
          now,
        ],
      );
    });

    return localFetchOrderById(orderId, ctx);
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to create takeaway order',
      data: undefined,
    };
  }
}

/**
 * Persist a server-returned order (from a Supabase RPC) into local SQLite so
 * that subsequent offline reads work immediately — without waiting for
 * PowerSync to sync the row down. Does NOT touch the counter (the RPC already
 * updated it in Postgres). Generates a CRUD PUT; when PowerSync uploads it,
 * the upsert is idempotent (row already exists in Supabase with the same id).
 */
export async function localInsertRpcOrder(order: SalesOrder): Promise<void> {
  await psExecute(
    `INSERT OR REPLACE INTO orders
       (id, bill_number, table_id, outlet_id, date, status_id,
        waiter_id, client_id, mode, created_by, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      order.order_id,
      order.bill_number,
      order.table_id || null,
      order.outlet_id,
      order.date,
      order.status_id,
      order.waiter_id,
      order.client_id ?? null,
      order.mode,
      order.created_by,
      order.created_at,
      order.updated_at,
    ],
  );
}

/**
 * Add an item to an order.
 * Stores `amount = quantity * unit_price` locally; PowerSync strips `amount`
 * from the upload (supabaseConnector.ts GENERATED_COLUMNS registry) so
 * Supabase computes it from the GENERATED ALWAYS expression.
 *
 * `item_name` is resolved via a JOIN to the local `menu_items` table (synced
 * by PowerSync). `department_name` and `category_name` come from the
 * `departments` and `menu_categories` tables (also synced).
 */
/**
 * Mark whether the current bill for an order has been printed. Set to true
 * right after a successful bill print, and reset to false whenever a new
 * item is added (see localAddOrderItem) since that invalidates the printout.
 */
export async function localSetOrderBillPrinted(
  orderId: string,
  printed: boolean,
): Promise<ApiResponse<boolean>> {
  const res = await psExecute(
    `UPDATE orders SET is_bill_printed = ?, updated_at = ? WHERE id = ?`,
    [printed ? 1 : 0, nowIso(), orderId],
  );
  if (!res.success) {
    return { success: false, error: res.error, data: false };
  }
  return { success: true, data: true };
}

export async function localAddOrderItem(
  orderId: string,
  menuItemId: string,
  quantity: number,
  unitPrice: number,
  notes: string | undefined,
  ctx: LocalOrderContext,
  /**
   * Fallback name applied when the menu_items table has not yet synced to local
   * SQLite (PowerSync sync is still catching up after login). Without this, the
   * LEFT JOIN returns NULL for item_name and the order panel shows a blank name.
   */
  _itemNameHint?: string,
): Promise<ApiResponse<SalesOrderItem>> {
  try {
    const id = genUUID();
    const now = nowIso();

    // If the order already has a client attached, new items must be priced
    // at the client's menu_percentage_rate, matching how the price is
    // recalculated for existing items when a client is assigned (see
    // localAssignClientToOrder) and the Postgres-side trigger that does the
    // same for any direct/synced write.
    const clientRateRow = await psGet<{
      menu_percentage_rate: string | number;
    } | null>(
      `SELECT c.menu_percentage_rate AS menu_percentage_rate
       FROM orders o
       LEFT JOIN clients c ON o.client_id = c.id
       WHERE o.id = ?`,
      [orderId],
    );
    const rate =
      clientRateRow?.menu_percentage_rate != null
        ? toNumber(clientRateRow.menu_percentage_rate) || 1
        : 1;
    const effectiveUnitPrice = Math.round(unitPrice * rate * 100) / 100;
    const amount = Math.round(quantity * effectiveUnitPrice * 100) / 100;

    const res = await psExecute(
      `INSERT INTO order_items
         (id, order_id, menu_item_id, quantity, unit_price, amount,
          notes, status, kot_id, added_by, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, 'PENDING', 0, ?, ?, ?)`,
      [
        id,
        orderId,
        menuItemId,
        quantity,
        effectiveUnitPrice,
        amount,
        notes?.trim() || null,
        ctx.userId,
        now,
        now,
      ],
    );

    if (!res.success) {
      return { success: false, error: res.error, data: undefined };
    }

    // Adding an item invalidates any bill already printed for this order.
    await localSetOrderBillPrinted(orderId, false);

    // Read back with all joined fields matching order_items_view shape.
    const row = await psGet<any>(
      `SELECT oi.*,
              mi.name          AS item_name,
              mi.department_id,
              d.name           AS department_name,
              mi.category_id,
              mc.name          AS category_name
       FROM order_items oi
       LEFT JOIN menu_items      mi ON mi.id = oi.menu_item_id
       LEFT JOIN departments      d  ON  d.id = mi.department_id
       LEFT JOIN menu_categories mc  ON mc.id = mi.category_id
       WHERE oi.id = ? LIMIT 1`,
      [id],
    );

    if (!row) {
      return {
        success: false,
        error: 'Item not found after insert',
        data: undefined,
      };
    }

    const mapped = mapLocalOrderItem(row);
    // Apply the hint only when the menu_items JOIN missed (row absent or not
    // synced yet) — mapLocalOrderItem falls back to UNAVAILABLE_ITEM_NAME in
    // that case, not an empty string, so check against that exact sentinel
    // rather than falsy-name (a real menu item could theoretically be named
    // anything, but never this literal placeholder).
    if (mapped.name === UNAVAILABLE_ITEM_NAME && _itemNameHint) {
      return { success: true, data: { ...mapped, name: _itemNameHint } };
    }
    return { success: true, data: mapped };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to add order item',
      data: undefined,
    };
  }
}

/**
 * Apply a discount to an order (replaces any existing discount — single
 * discount model enforced by the schema's unique constraint).
 * Equivalent to `add_order_discount` RPC.
 */
export async function localAddOrderDiscount(
  orderId: string,
  value: number,
  type: DiscountInputType,
  notes: string | undefined,
  ctx: LocalOrderContext,
): Promise<ApiResponse<OrderDiscount>> {
  try {
    // Compute the running total (non-cancelled items).
    const totalRow = await psGet<any>(
      `SELECT COALESCE(SUM(${orderItemAmountSql('oi')}), 0) AS total
       FROM order_items oi
       WHERE oi.order_id = ? AND oi.status != 'CANCELLED'`,
      [orderId],
    );
    const total = toNumber(totalRow?.total);

    let discountAmount: number;
    if (type === 'PERCENTAGE') {
      discountAmount = Math.round(((total * value) / 100) * 100) / 100;
    } else {
      discountAmount = Math.min(value, total);
    }

    if (discountAmount <= 0) {
      return {
        success: false,
        error: 'Discount amount must be greater than zero',
        data: undefined,
      };
    }

    const now = nowIso();

    // Remove any existing discount (single-discount-per-order model).
    await psExecute(`DELETE FROM discounts WHERE order_id = ?`, [orderId]);

    const id = genUUID();
    await psExecute(
      `INSERT INTO discounts
         (id, order_id, amount, added_by, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        orderId,
        discountAmount,
        ctx.userId,
        notes?.trim() || null,
        now,
        now,
      ],
    );

    const row = await psGet<any>(
      `SELECT * FROM discounts WHERE id = ? LIMIT 1`,
      [id],
    );

    if (!row) {
      return {
        success: false,
        error: 'Discount not found after insert',
        data: undefined,
      };
    }

    return {
      success: true,
      data: {
        id: row.id,
        order_id: row.order_id,
        amount: toNumber(row.amount),
        added_by: row.added_by,
        notes: row.notes ?? null,
        created_at: row.created_at,
        updated_at: row.updated_at,
      },
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to add discount',
      data: undefined,
    };
  }
}

/**
 * Remove the discount from an order.
 * Equivalent to `remove_order_discount` RPC.
 */
export async function localRemoveOrderDiscount(
  orderId: string,
): Promise<ApiResponse<boolean>> {
  try {
    const res = await psExecute(`DELETE FROM discounts WHERE order_id = ?`, [
      orderId,
    ]);

    if (!res.success) {
      return { success: false, error: res.error, data: false };
    }

    return { success: true, data: (res.rowsAffected ?? 0) > 0 };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to remove discount',
      data: false,
    };
  }
}

/**
 * Confirm all PENDING items on an order, assigning them the next KOT number,
 * and create one `kitchen_tickets` row per distinct department present in the
 * confirmed batch (mirrors the online `confirm_order_kot` RPC, which does the
 * same insert server-side — see
 * supabase/migrations/20260723115352_add_kitchen_tickets_to_confirm_order_kot.sql).
 *
 * Wrapped in a single `db.writeTransaction` (previously this used loose
 * `psGet`/`psExecute` calls with no lock — a pre-existing atomicity bug that
 * could let two concurrent local confirmations on the same order race on the
 * counter read). `writeTransaction` acquires PowerSync's write lock so the
 * counter read/increment, item update, and ticket inserts all commit as one
 * unit, exactly like `localGetOrCreateTableOrder` above.
 *
 * `physicalKotNumber` is optional — only passed when the outlet has
 * `kot_tracking_enabled` and the cashier entered a value in
 * `KotPhysicalNumberModal`. Stamped onto every item in the same batch
 * `UPDATE` that already assigns `kot_id`, so it stays atomic with the
 * confirm itself. KOT confirm is Category A (local-only) end to end — there
 * is no separate online RPC call for this action, so PowerSync's normal
 * background upload (via the existing `order_items` UPDATE grant/RLS) is
 * what propagates these two new columns to Postgres, exactly like `kot_id`
 * itself already does.
 */
export async function localConfirmOrderKot(
  orderId: string,
  ctx: LocalOrderContext,
  physicalKotNumber?: string,
): Promise<ApiResponse<OrderKotConfirmation>> {
  try {
    const now = nowIso();
    let newKotId = 0;
    let confirmedCount = 0;

    await db.writeTransaction(async (tx) => {
      const pendingRow = await tx.get<any>(
        `SELECT COUNT(*) AS cnt FROM order_items WHERE order_id = ? AND status = 'PENDING'`,
        [orderId],
      );
      const pendingCount = toNumber(pendingRow?.cnt);
      if (pendingCount === 0) {
        throw new Error('No pending items to confirm');
      }

      const orderRow = await tx.get<any>(
        `SELECT date FROM orders WHERE id = ? LIMIT 1`,
        [orderId],
      );
      const orderDate = orderRow?.date || todayDate();

      const kotRow = await tx.get<any>(
        `SELECT last_kot_id FROM outlet_kot_counters WHERE id = ? LIMIT 1`,
        [ctx.outletId],
      );
      if (!kotRow) {
        throw new Error(
          'KOT counter not found — sync may not be complete. Please wait and try again.',
        );
      }
      newKotId = toNumber(kotRow.last_kot_id) + 1;

      await tx.execute(
        `UPDATE outlet_kot_counters SET last_kot_id = ?, updated_at = ? WHERE id = ?`,
        [newKotId, now, ctx.outletId],
      );

      const updateRes = await tx.execute(
        `UPDATE order_items
         SET status = 'CONFIRMED', kot_id = ?, physical_kot_number = ?, physical_kot_recorded_by = ?, updated_at = ?
         WHERE order_id = ? AND status = 'PENDING'`,
        [
          newKotId,
          physicalKotNumber ?? null,
          physicalKotNumber ? ctx.userId : null,
          now,
          orderId,
        ],
      );
      confirmedCount = updateRes.rowsAffected ?? pendingCount;

      // Outlet-level capability check — KDS requires an 8GB+ RAM device, so
      // it's opt-in per outlet (see LocalOrderContext.isKdsEnabled). Skipping
      // this insert entirely when disabled means no kitchen_tickets rows are
      // ever created (and therefore nothing to sync/upload) for that outlet.
      if (ctx.isKdsEnabled) {
        const departmentResult = await tx.execute(
          `SELECT DISTINCT mi.department_id AS department_id
           FROM order_items oi
           JOIN menu_items mi ON mi.id = oi.menu_item_id
           WHERE oi.order_id = ? AND oi.kot_id = ?`,
          [orderId, newKotId],
        );
        // eslint-disable-next-line no-underscore-dangle
        const departmentRows = (departmentResult.rows?._array ?? []) as {
          department_id: string | null;
        }[];

        // Sequential (not Promise.all) — these execute() calls share the
        // same open write transaction, which must not receive concurrent
        // calls.
        await departmentRows.reduce(
          async (previous, { department_id: departmentId }) => {
            await previous;
            await tx.execute(
              `INSERT INTO kitchen_tickets
               (id, order_id, outlet_id, kot_id, department_id, order_date, status, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, 'NEW', ?, ?)`,
              [
                genUUID(),
                orderId,
                ctx.outletId,
                newKotId,
                departmentId ?? null,
                orderDate,
                now,
                now,
              ],
            );
          },
          Promise.resolve(),
        );
      }
    });

    return {
      success: true,
      data: { kot_id: newKotId, confirmed_count: confirmedCount },
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to confirm KOT',
      data: undefined,
    };
  }
}

/**
 * Update the notes on a PENDING order item.
 * Equivalent to `update_pending_order_item_notes` RPC.
 *
 * Wrapped in `db.writeTransaction` (previously a bare `psExecute`) so the
 * existence/status check and the update happen as one atomic read-then-write
 * against the same connection, closing any theoretical window where a
 * concurrent write (e.g. a KOT confirmation on the same order, which also now
 * runs inside its own writeTransaction — see localConfirmOrderKot) could be
 * mid-flight. Also reports the *actual* current status on failure instead of
 * a generic "not found or not pending" message, so a status mismatch is
 * immediately diagnosable rather than looking identical to a missing row.
 */
export async function localUpdateOrderItemNotes(
  orderItemId: string,
  notes: string,
): Promise<ApiResponse<SalesOrderItem>> {
  try {
    const now = nowIso();

    await db.writeTransaction(async (tx) => {
      const existing = await tx.get<{ status: string } | undefined>(
        `SELECT status FROM order_items WHERE id = ? LIMIT 1`,
        [orderItemId],
      );

      if (!existing) {
        throw new Error('Order item not found');
      }
      if (existing.status !== 'PENDING') {
        throw new Error(
          `Cannot update notes: item is already ${existing.status.toLowerCase()}`,
        );
      }

      await tx.execute(
        `UPDATE order_items
         SET notes = ?, updated_at = ?
         WHERE id = ? AND status = 'PENDING'`,
        [notes?.trim() || null, now, orderItemId],
      );
    });

    const row = await psGet<any>(
      `SELECT oi.*,
              mi.name  AS item_name,
              mi.department_id,
              d.name   AS department_name,
              mi.category_id,
              mc.name  AS category_name
       FROM order_items oi
       LEFT JOIN menu_items      mi ON mi.id = oi.menu_item_id
       LEFT JOIN departments      d  ON  d.id = mi.department_id
       LEFT JOIN menu_categories mc  ON mc.id = mi.category_id
       WHERE oi.id = ? LIMIT 1`,
      [orderItemId],
    );

    return row
      ? { success: true, data: mapLocalOrderItem(row) }
      : {
          success: false,
          error: 'Item not found after update',
          data: undefined,
        };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to update item notes',
      data: undefined,
    };
  }
}

/**
 * Remove or cancel an order item.
 *  - PENDING  → hard DELETE from order_items
 *  - CONFIRMED → UPDATE status = 'CANCELLED' + INSERT item_cancellation record
 * Equivalent to `remove_order_item` RPC.
 */
export async function localRemoveOrderItem(
  orderItemId: string,
  reason: string | undefined,
  ctx: LocalOrderContext,
): Promise<ApiResponse<SalesOrderItem>> {
  try {
    // Fetch the item first so we can return it and check its status.
    const row = await psGet<any>(
      `SELECT oi.*,
              mi.name  AS item_name,
              mi.department_id,
              d.name   AS department_name,
              mi.category_id,
              mc.name  AS category_name
       FROM order_items oi
       LEFT JOIN menu_items      mi ON mi.id = oi.menu_item_id
       LEFT JOIN departments      d  ON  d.id = mi.department_id
       LEFT JOIN menu_categories mc  ON mc.id = mi.category_id
       WHERE oi.id = ? LIMIT 1`,
      [orderItemId],
    );

    if (!row) {
      return { success: false, error: 'Order item not found', data: undefined };
    }

    const now = nowIso();

    if (row.status === 'PENDING') {
      // Hard delete — PowerSync sends DELETE to Supabase.
      await psExecute(`DELETE FROM order_items WHERE id = ?`, [orderItemId]);
    } else if (row.status === 'CONFIRMED') {
      if (!reason?.trim()) {
        return {
          success: false,
          error: 'Cancellation reason is required for confirmed items',
          data: undefined,
        };
      }

      // Soft cancel.
      await psExecute(
        `UPDATE order_items SET status = 'CANCELLED', updated_at = ? WHERE id = ?`,
        [now, orderItemId],
      );

      // Record the cancellation.
      await psExecute(
        `INSERT INTO item_cancellations
           (id, menu_item_id, cancelled_by, reason, order_id, created_at)
         VALUES (?, ?, ?, ?, ?, ?)`,
        [
          genUUID(),
          row.menu_item_id,
          ctx.userId,
          reason.trim(),
          row.order_id,
          now,
        ],
      );
    } else {
      return {
        success: false,
        error: 'Cannot remove a cancelled item',
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapLocalOrderItem({ ...row, updated_at: now }),
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to remove item',
      data: undefined,
    };
  }
}

/**
 * Update the waiter assigned to an order.
 * Equivalent to `orders.update({ waiter_id })`.
 */
export async function localUpdateOrderWaiter(
  orderId: string,
  waiterId: string,
): Promise<ApiResponse<boolean>> {
  try {
    const res = await psExecute(
      `UPDATE orders SET waiter_id = ?, updated_at = ? WHERE id = ?`,
      [waiterId, nowIso(), orderId],
    );

    if (!res.success) {
      return { success: false, error: res.error, data: false };
    }

    return { success: true, data: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to update waiter',
      data: false,
    };
  }
}

/**
 * Assign a client to an order (or clear it via clientId = null).
 * Equivalent to `orders.update({ client_id })`, plus repricing every open
 * order_item against the client's menu_percentage_rate (rate 1 / base price
 * when clientId is null) — mirrors the Postgres-side trigger
 * `recalculate_order_item_prices_on_client_change` so the price update is
 * instant even fully offline. Both statements are wrapped in a single
 * writeTransaction so PowerSync's reactive watched queries never observe an
 * order with the new client_id but stale (not-yet-repriced) item prices.
 */
export async function localAssignClientToOrder(
  orderId: string,
  clientId: string | null,
): Promise<ApiResponse<boolean>> {
  try {
    const now = nowIso();

    let rate = 1;
    if (clientId) {
      const clientRow = await psGet<{ menu_percentage_rate: string | number }>(
        `SELECT menu_percentage_rate FROM clients WHERE id = ?`,
        [clientId],
      );
      rate = clientRow ? toNumber(clientRow.menu_percentage_rate) || 1 : 1;
    }

    await db.writeTransaction(async (tx) => {
      await tx.execute(
        `UPDATE orders SET client_id = ?, updated_at = ? WHERE id = ? AND status_id = ?`,
        [clientId, now, orderId, OPEN_STATUS_ID],
      );

      await tx.execute(
        `UPDATE order_items
         SET unit_price = ROUND(
               (SELECT mi.base_price FROM menu_items mi WHERE mi.id = order_items.menu_item_id) * ?,
             2),
             updated_at = ?
         WHERE order_id = ? AND status != 'CANCELLED'`,
        [rate, now, orderId],
      );
    });

    return { success: true, data: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to assign client',
      data: false,
    };
  }
}

/**
 * Shift an entire DINE order to a different table.
 * Equivalent to `shift_order_to_table` RPC.
 * Note: The server RPC validates outlet membership; the local version
 * trusts the UI-layer guard.
 */
export async function localShiftOrderToTable(
  orderId: string,
  targetTableId: number,
): Promise<ApiResponse<boolean>> {
  try {
    // Guard against shifting an order onto a table that already has its own
    // OPEN order — checked inside the writeTransaction so a concurrent shift/
    // table-open on the target table can't race past this check (same TOCTOU
    // class as localGetOrCreateTableOrder).
    await db.writeTransaction(async (tx) => {
      const existing = await tx.get<{ id: string } | undefined>(
        `SELECT id FROM orders WHERE table_id = ? AND status_id = ? AND id != ?
         ORDER BY created_at DESC LIMIT 1`,
        [targetTableId, OPEN_STATUS_ID, orderId],
      );
      if (existing) {
        throw new Error('Target table already has an open order');
      }

      await tx.execute(
        `UPDATE orders SET table_id = ?, updated_at = ? WHERE id = ?`,
        [targetTableId, nowIso(), orderId],
      );
    });

    return { success: true, data: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to shift order',
      data: false,
    };
  }
}

// ── Table state helpers ───────────────────────────────────────────────────────

/**
 * Return the table_id of every open DINE order for the outlet.
 * Used to highlight occupied tables from local SQLite, so the sales page
 * reflects locally-created orders even before they upload to Supabase.
 */
export async function localFetchOpenDineOrderTableIds(
  outletId: string,
): Promise<number[]> {
  try {
    const rows = await psGetAll<{ table_id: string | number }>(
      `SELECT DISTINCT table_id
       FROM orders
       WHERE outlet_id = ? AND status_id = ? AND mode = 'DINE' AND table_id IS NOT NULL`,
      [outletId, OPEN_STATUS_ID],
    );
    return rows.map((r) => toNumber(r.table_id)).filter((id) => id > 0);
  } catch {
    return [];
  }
}

// ── Local settlement helpers (Category A — offline-capable) ──────────────────
//
// Each function:
//  1. INSERTs a row into `order_settlements` (PowerSync uploads when online)
//  2. UPDATEs `orders.status_id` to the payment method ID so the order is
//     no longer returned by localFetchRunningTableOrder (which filters by
//     status_id = OPEN_STATUS_ID = 9) — table unhighlights immediately.
//
// Server-side validation (credit accounting, duplicate detection, etc.) runs
// when PowerSync flushes the write queue on reconnect. Fatal constraint
// violations are discarded by the connector; transient errors are retried.

/**
 * Settle an order with a single payment method (direct / cash / card / etc.).
 *
 * Wrapped in `db.writeTransaction` so the "order is still OPEN" check and the
 * settlement insert + status update commit as one atomic unit — every online
 * settlement RPC (direct_settlement, settle_credit_bill, etc.) validates the
 * order's current status server-side before writing; this local path had no
 * equivalent guard, which let a duplicate local write (e.g. a double-tap
 * while offline) queue a second order_settlements row for an order that was
 * already settled, producing a duplicate revenue journal entry once synced.
 */
export async function localSettleOrder(
  orderId: string,
  netAmount: number,
  paymentMethodId: number,
  notes: string | null,
  ctx: LocalOrderContext,
  clientId?: string | null,
): Promise<ApiResponse<boolean>> {
  try {
    const now = nowIso();

    await db.writeTransaction(async (tx) => {
      const orderRow = await tx.get<{ status_id: number } | undefined>(
        `SELECT status_id FROM orders WHERE id = ? LIMIT 1`,
        [orderId],
      );

      if (!orderRow) {
        throw new Error('Order not found');
      }
      if (toNumber(orderRow.status_id) !== OPEN_STATUS_ID) {
        throw new Error('Order is no longer open for settlement');
      }

      await tx.execute(
        `INSERT INTO order_settlements
           (id, order_id, amount, payment_method_id, notes, created_by, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [
          genUUID(),
          orderId,
          netAmount,
          paymentMethodId,
          notes ?? null,
          ctx.userId,
          now,
        ],
      );

      if (clientId) {
        await tx.execute(
          `UPDATE orders SET status_id = ?, client_id = ?, updated_at = ? WHERE id = ?`,
          [paymentMethodId, clientId, now, orderId],
        );
      } else {
        await tx.execute(
          `UPDATE orders SET status_id = ?, updated_at = ? WHERE id = ?`,
          [paymentMethodId, now, orderId],
        );
      }
    });

    return { success: true, data: true };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to settle order',
      data: false,
    };
  }
}

/**
 * Settle an order across multiple payment methods (split settlement).
 * Each payment becomes a separate order_settlements row.
 * The order's status_id is set to the largest payment's method ID.
 *
 * Wrapped in `db.writeTransaction` with the same "order is still OPEN" guard
 * as `localSettleOrder` — see that function's comment for why.
 */
export async function localSplitSettleOrder(
  orderId: string,
  payments: { paymentMethodId: number; amount: number }[],
  notes: string | null,
  ctx: LocalOrderContext,
): Promise<ApiResponse<boolean>> {
  try {
    if (payments.length === 0) {
      return { success: false, error: 'No payments provided', data: false };
    }

    const now = nowIso();

    await db.writeTransaction(async (tx) => {
      const orderRow = await tx.get<{ status_id: number } | undefined>(
        `SELECT status_id FROM orders WHERE id = ? LIMIT 1`,
        [orderId],
      );

      if (!orderRow) {
        throw new Error('Order not found');
      }
      if (toNumber(orderRow.status_id) !== OPEN_STATUS_ID) {
        throw new Error('Order is no longer open for settlement');
      }

      // Sequential (not Promise.all) — these execute() calls share the same
      // open write transaction, which must not receive concurrent calls.
      await payments.reduce(async (previous, { paymentMethodId, amount }) => {
        await previous;
        await tx.execute(
          `INSERT INTO order_settlements
             (id, order_id, amount, payment_method_id, notes, created_by, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?)`,
          [
            genUUID(),
            orderId,
            amount,
            paymentMethodId,
            notes ?? null,
            ctx.userId,
            now,
          ],
        );
      }, Promise.resolve());

      // Update order status to the payment method with the highest amount.
      const primaryMethodId = payments.reduce((best, p) =>
        p.amount > best.amount ? p : best,
      ).paymentMethodId;

      await tx.execute(
        `UPDATE orders SET status_id = ?, updated_at = ? WHERE id = ?`,
        [primaryMethodId, now, orderId],
      );
    });

    return { success: true, data: true };
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to split-settle order',
      data: false,
    };
  }
}

/**
 * Settle an order on credit (company / client pays later).
 * Resolves the "CREDIT BILL" payment method from local SQLite.
 */
export async function localCreditSettleOrder(
  orderId: string,
  netAmount: number,
  notes: string | null,
  ctx: LocalOrderContext,
  clientId: string,
): Promise<ApiResponse<boolean>> {
  try {
    // Look up the credit payment method from the local payment_methods table.
    const pmRow = await psGet<{ id: string }>(
      `SELECT id FROM payment_methods
       WHERE UPPER(name) IN ('CREDIT BILL','CREDIT','COMPANY CREDIT')
       ORDER BY CAST(id AS INTEGER) ASC
       LIMIT 1`,
      [],
    );

    if (!pmRow) {
      return {
        success: false,
        error: 'Credit payment method not found in local data',
        data: false,
      };
    }

    return localSettleOrder(
      orderId,
      netAmount,
      toNumber(pmRow.id),
      notes,
      ctx,
      clientId,
    );
  } catch (err) {
    return {
      success: false,
      error:
        err instanceof Error ? err.message : 'Failed to credit-settle order',
      data: false,
    };
  }
}

/**
 * Settle an order via a named payment method (check, EFT, voucher, etc.).
 * `settlementTypeName` must match a `payment_methods.name` value (case-insensitive).
 */
export async function localOtherSettleOrder(
  orderId: string,
  netAmount: number,
  settlementTypeName: string,
  notes: string | null,
  ctx: LocalOrderContext,
): Promise<ApiResponse<boolean>> {
  try {
    const pmRow = await psGet<{ id: string }>(
      `SELECT id FROM payment_methods
       WHERE UPPER(name) = UPPER(?)
       LIMIT 1`,
      [settlementTypeName],
    );

    if (!pmRow) {
      return {
        success: false,
        error: `Payment method "${settlementTypeName}" not found in local data`,
        data: false,
      };
    }

    return localSettleOrder(orderId, netAmount, toNumber(pmRow.id), notes, ctx);
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to settle order',
      data: false,
    };
  }
}

/**
 * Seed local SQLite with the result of an online settlement RPC
 * (direct_settlement / split_settlement / company_settlement /
 * other_settlement), so the local read model reflects it immediately instead
 * of waiting for PowerSync's downstream sync to pull it back down. The RPC
 * already updated orders.status_id server-side — `newStatusId` must match
 * whatever the RPC actually chose (e.g. split_settlement uses the FIRST
 * payment method in the array, not the largest amount like
 * localSplitSettleOrder does), so callers pass it explicitly rather than
 * this function re-deriving it.
 */
export async function localSeedRpcSettlement(
  settlements: OrderSettlement | OrderSettlement[],
  orderId: string,
  newStatusId: number,
  clientId?: string | null,
): Promise<void> {
  const rows = Array.isArray(settlements) ? settlements : [settlements];
  const now = nowIso();

  await db.writeTransaction(async (tx) => {
    await rows.reduce(async (previous, settlement) => {
      await previous;
      await tx.execute(
        `INSERT OR REPLACE INTO order_settlements
           (id, order_id, amount, payment_method_id, notes, created_by, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [
          settlement.id,
          settlement.order_id,
          settlement.amount,
          settlement.payment_method_id,
          settlement.notes ?? null,
          settlement.created_by,
          settlement.created_at,
        ],
      );
    }, Promise.resolve());

    if (clientId) {
      await tx.execute(
        `UPDATE orders SET status_id = ?, client_id = ?, updated_at = ? WHERE id = ?`,
        [newStatusId, clientId, now, orderId],
      );
    } else {
      await tx.execute(
        `UPDATE orders SET status_id = ?, updated_at = ? WHERE id = ?`,
        [newStatusId, now, orderId],
      );
    }
  });
}

// ── Section / table reads (offline-capable) ──────────────────────────────────

/**
 * Fetch all sections for an outlet from local SQLite.
 * Replaces the Supabase `outlet_sections_view` call in sectionService.ts.
 */
export async function localFetchOutletSections(
  outletId: string,
): Promise<OutletSection[]> {
  const rows = await psGetAll<any>(
    `SELECT os.id, os.name, os.outlet_id, os.is_hidden,
            os.created_at, os.updated_at,
            (SELECT COUNT(*) FROM outlet_tables ot WHERE ot.section_id = os.id) AS table_count
     FROM outlet_sections os
     WHERE os.outlet_id = ?
     ORDER BY os.name ASC`,
    [outletId],
  );
  return rows.map((r) => ({
    id: toNumber(r.id),
    name: r.name || '',
    outlet_id: r.outlet_id || outletId,
    is_hidden: Boolean(toNumber(r.is_hidden)),
    created_at: r.created_at || '',
    updated_at: r.updated_at || '',
    table_count: toNumber(r.table_count),
  }));
}

/**
 * Fetch all tables for an outlet from local SQLite, with section info and
 * the current OPEN order's bill_number (if any). Replaces the Supabase
 * `outlet_tables_view` call in sectionService.ts.
 */
export async function localFetchOutletTables(
  outletId: string,
): Promise<OutletTable[]> {
  const rows = await psGetAll<any>(
    `SELECT ot.id, ot.name, ot.section_id, ot.is_hidden,
            ot.created_at, ot.updated_at,
            os.name      AS section_name,
            os.outlet_id AS outlet_id,
            CASE WHEN o.id IS NOT NULL THEN 1 ELSE 0 END AS has_running_order,
            o.bill_number,
            o.is_bill_printed
     FROM outlet_tables ot
     LEFT JOIN outlet_sections os ON ot.section_id = os.id
     LEFT JOIN orders o
       ON CAST(o.table_id AS TEXT) = ot.id
      AND o.outlet_id = ?
      AND o.status_id = ${OPEN_STATUS_ID}
     WHERE os.outlet_id = ?
     ORDER BY ot.name ASC`,
    [outletId, outletId],
  );
  return rows.map((r) => ({
    id: toNumber(r.id),
    name: r.name || '',
    section_id: toNumber(r.section_id),
    section_name: r.section_name || '',
    outlet_id: r.outlet_id || outletId,
    is_hidden: Boolean(toNumber(r.is_hidden)),
    has_running_order: Boolean(toNumber(r.has_running_order)),
    bill_number: r.bill_number != null ? String(r.bill_number) : undefined,
    is_bill_printed: Boolean(toNumber(r.is_bill_printed)),
    created_at: r.created_at || '',
    updated_at: r.updated_at || '',
  }));
}

// ── Connectivity helper ───────────────────────────────────────────────────────

/**
 * Check if PowerSync currently has an active sync connection.
 * Returns true when online, false when offline or not yet synced.
 */
export async function isOnline(): Promise<boolean> {
  try {
    return db.currentStatus.connected;
  } catch {
    return navigator.onLine;
  }
}
